// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Estoque/PosicaoEstoque.tsx
//  Posição atual do estoque com paginação, busca e filtro por local
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { Search, Package, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEstoquePosicao } from '@/features/estoque/hooks/useEstoquePosicao';
import { useLocalEstoqueCombo } from '@/features/estoque/hooks/useLocalEstoque';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const PAGE_SIZES = [10, 20, 50] as const;

const PosicaoEstoque = () => {
  const [busca,          setBusca]          = useState('');
  const [buscaInput,     setBuscaInput]     = useState('');
  const [localId,        setLocalId]        = useState(0);
  const [pagina,         setPagina]         = useState(1);
  const [tamanhoPagina,  setTamanhoPagina]  = useState(20);

  const { data: paginado, isLoading, isFetching } = useEstoquePosicao({
    pagina, tamanhoPagina, busca, localId,
  });
  const { data: locais } = useLocalEstoqueCombo();

  const itens        = paginado?.itens       ?? [];
  const total        = paginado?.total        ?? 0;
  const totalPaginas = paginado?.totalPaginas ?? 1;
  const firstItem    = (pagina - 1) * tamanhoPagina + 1;
  const lastItem     = Math.min(pagina * tamanhoPagina, total);

  const handleBusca = () => { setBusca(buscaInput); setPagina(1); };
  const handleLocalChange = (value: string) => { setLocalId(Number(value)); setPagina(1); };
  const handlePageSizeChange = (value: string) => { setTamanhoPagina(Number(value)); setPagina(1); };

  const saldoBadge = (saldo: number, minimo: number) => {
    if (saldo <= 0)         return <Badge className="bg-red-500/10 text-red-600">Sem Saldo</Badge>;
    if (saldo < minimo)     return <Badge className="bg-orange-500/10 text-orange-600">Abaixo do Mín.</Badge>;
    return <Badge className="bg-green-500/10 text-green-600">Normal</Badge>;
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Posição de Estoque</h1>
        <p className="text-muted-foreground mt-1">Saldo atual por produto e local de armazenagem</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />Posição Atual
            </CardTitle>
            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar produto..."
                  value={buscaInput}
                  onChange={(e) => setBuscaInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleBusca()}
                  className="pl-9"
                />
              </div>
              <Button variant="outline" onClick={handleBusca}>Buscar</Button>
              <Select value={String(localId)} onValueChange={handleLocalChange}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="Todos os locais" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Todos os locais</SelectItem>
                  {locais?.map((l) => (
                    <SelectItem key={l.id} value={String(l.id)}>{l.descricao}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produto</TableHead>
                  <TableHead>Cód. Barras</TableHead>
                  <TableHead>Local</TableHead>
                  <TableHead className="text-right">Saldo Atual</TableHead>
                  <TableHead className="text-right">Reservado</TableHead>
                  <TableHead className="text-right">Mínimo</TableHead>
                  <TableHead className="text-right">Máximo</TableHead>
                  <TableHead className="text-right">Custo Médio</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
                ) : itens.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Nenhum item encontrado</TableCell></TableRow>
                ) : (
                  itens.map((item, idx) => (
                    <TableRow key={`${item.subProdutoId}-${item.localEstoqueId}-${idx}`}>
                      <TableCell className="font-medium">{item.descricao}</TableCell>
                      <TableCell className="text-muted-foreground">{item.codigoBarras || '—'}</TableCell>
                      <TableCell>{item.localDescricao}</TableCell>
                      <TableCell className="text-right font-medium">{item.saldoAtual.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</TableCell>
                      <TableCell className="text-right">{item.saldoReservado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</TableCell>
                      <TableCell className="text-right">{item.estoqueMinimo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</TableCell>
                      <TableCell className="text-right">{item.estoqueMaximo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</TableCell>
                      <TableCell className="text-right">{item.custoMedio.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</TableCell>
                      <TableCell>{saldoBadge(item.saldoAtual, item.estoqueMinimo)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Paginação */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
            <p className="text-sm text-muted-foreground">
              {total === 0 ? 'Nenhum registro'
                : `Mostrando ${firstItem}–${lastItem} de ${total} registro${total !== 1 ? 's' : ''}`}
              {isFetching && !isLoading && <span className="ml-2 text-xs">(atualizando...)</span>}
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">Itens por página:</span>
                <Select value={String(tamanhoPagina)} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="w-[80px] h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(1)} disabled={pagina === 1}>«</Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p - 1)} disabled={pagina === 1}><ChevronLeft className="h-4 w-4" /></Button>
                {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                  let page: number;
                  if      (totalPaginas <= 5)         page = i + 1;
                  else if (pagina <= 3)                page = i + 1;
                  else if (pagina >= totalPaginas - 2) page = totalPaginas - 4 + i;
                  else                                 page = pagina - 2 + i;
                  return (
                    <Button key={page} variant={pagina === page ? 'default' : 'outline'}
                      size="icon" className="h-8 w-8 text-xs" onClick={() => setPagina(page)}>{page}</Button>
                  );
                })}
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p + 1)} disabled={pagina >= totalPaginas}><ChevronRight className="h-4 w-4" /></Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(totalPaginas)} disabled={pagina >= totalPaginas}>»</Button>
              </div>
              <span className="text-sm text-muted-foreground whitespace-nowrap">Página {pagina} de {totalPaginas}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PosicaoEstoque;
